import { useCallback, useMemo } from 'react';
import { useClinic } from '@/stores/ClinicContext';
import { orderUnits } from '@/utils/salas';
import type { Unit } from '@/types/models';
import {
  ALL_UNITS, appointmentUnitId, filterByUnit, resolveAgendaUnit, unitTagNames,
} from '@/utils/agendaUnit';

/**
 * A unidade que a Agenda mostra (spec 019, fase 2) — a mesma pra grade da
 * AgendaPage e pro painel lateral (DayAgendaDrawer), que assinam os
 * agendamentos cada um por si. Regra em `utils/agendaUnit.ts`.
 */
const NO_UNITS: Unit[] = [];

export function useAgendaUnit() {
  const {
    units = NO_UNITS, accessibleUnits = NO_UNITS, allUnitsAccess = false,
    currentUnitId, setCurrentUnitId, allUnitsView = false, setAllUnitsView,
  } = useClinic();

  // Todas as ativas pra regra (de que unidade é cada agendamento); o
  // seletor só oferece as que esta conta acessa.
  const ordered = useMemo(() => orderUnits(units), [units]);
  const unitIds = useMemo(() => ordered.map((u) => u.id), [ordered]);
  const multiUnit = unitIds.length > 1;
  const canSeeAll = allUnitsAccess;
  const unitId = resolveAgendaUnit({ unitIds, currentUnitId, allUnits: allUnitsView, canSeeAll });
  const tagNames = useMemo(() => unitTagNames(ordered, unitId), [ordered, unitId]);

  /** Só os agendamentos da unidade (todos em "Todas" ou com uma unidade). */
  const filter = useCallback(
    <T extends { unitId?: string | null }>(apts: T[]) => filterByUnit(apts, unitId, unitIds),
    [unitId, unitIds],
  );

  /** Etiqueta da unidade pro cartão — só em "Todas" com 2+ unidades. */
  const tagFor = useCallback(
    (a: { unitId?: string | null }) => tagNames?.[appointmentUnitId(a, unitIds)],
    [tagNames, unitIds],
  );

  /** Troca pelo seletor da Agenda: uma unidade vira a unidade atual do
   *  computador (a mesma do Topbar); `ALL_UNITS` liga "Todas". */
  const select = useCallback((value: string) => {
    if (value === ALL_UNITS) {
      setAllUnitsView(true);
      return;
    }
    setAllUnitsView(false);
    setCurrentUnitId(value);
  }, [setAllUnitsView, setCurrentUnitId]);

  return {
    /** Unidades do seletor: as que esta conta acessa, na ordem das telas
     *  (Sede, outras, externas). */
    units: accessibleUnits,
    multiUnit,
    canSeeAll,
    /** `null` = todas. */
    unitId,
    /** Valor do `<select>`: a unidade, ou `ALL_UNITS`. */
    selectValue: unitId ?? ALL_UNITS,
    select,
    filter,
    tagFor,
    /** Nome por id pros cartões, ou `undefined` (sem etiqueta). */
    tagNames,
  };
}
